/**
 * API Client
 * Centralized HTTP client with customer + admin silent refresh on 401.
 */

import { apiKeyConfig } from '@/shared/utils/apiKeyConfig'
import {
  clearAdminSession,
  clearSession,
  getAdminRefreshToken,
  getAuthToken,
  getRefreshToken,
  isAccessTokenExpired,
  notifyAuthLost,
  setAdminTokens,
  setAuthTokens,
} from '@/shared/utils/session'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1'

export interface ApiError {
  message: string
  status: number
  errors?: Record<string, string[]>
}

function ngrokRequestHeaders(): Record<string, string> {
  if (typeof window === 'undefined') return {}
  const host = window.location.hostname
  if (
    host.includes('ngrok-free.dev') ||
    host.includes('ngrok-free.app') ||
    host.includes('ngrok.io') ||
    host.endsWith('.ngrok.app')
  ) {
    return { 'ngrok-skip-browser-warning': 'true' }
  }
  return {}
}

type RefreshResult = { accessToken: string; refreshToken?: string }

let customerRefreshInFlight: Promise<RefreshResult | null> | null = null
let adminRefreshInFlight: Promise<RefreshResult | null> | null = null

function shouldSkipCustomerRefresh(endpoint: string): boolean {
  if (endpoint.startsWith('/admin')) return true
  if (endpoint === '/auth/refresh' || endpoint.startsWith('/auth/refresh?')) return true
  if (endpoint.startsWith('/auth/admin/')) return true
  if (endpoint.startsWith('/auth/customer/checkout/')) return true
  if (endpoint.startsWith('/auth/otp/')) return true
  if (endpoint.startsWith('/auth/customer/login/')) return true
  if (endpoint.startsWith('/auth/customer/register/')) return true
  if (endpoint.startsWith('/auth/google')) return true
  if (endpoint.startsWith('/auth/apple')) return true
  if (endpoint.startsWith('/auth/facebook')) return true
  if (endpoint.startsWith('/auth/email/')) return true
  if (endpoint.startsWith('/auth/password')) return true
  if (endpoint === '/auth/logout' || endpoint.startsWith('/auth/logout?')) return true
  return false
}

function shouldSkipAdminRefresh(endpoint: string): boolean {
  if (endpoint.startsWith('/auth/admin/login')) return true
  if (endpoint.startsWith('/auth/admin/refresh')) return true
  if (endpoint.startsWith('/auth/admin/create-initial')) return true
  return false
}

async function refreshCustomerTokens(baseURL: string): Promise<RefreshResult | null> {
  if (customerRefreshInFlight) return customerRefreshInFlight

  customerRefreshInFlight = (async () => {
    const refreshToken = getRefreshToken()
    if (!refreshToken) return null

    try {
      const response = await fetch(`${baseURL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...ngrokRequestHeaders(),
        },
        body: JSON.stringify({ refreshToken }),
      })

      if (!response.ok) return null

      const body = await response.json().catch(() => ({}))
      const data = body?.data ?? body
      const accessToken = data?.token || data?.accessToken
      const nextRefresh = data?.refreshToken
      if (!accessToken || typeof accessToken !== 'string') return null

      setAuthTokens(accessToken, typeof nextRefresh === 'string' ? nextRefresh : refreshToken)
      return {
        accessToken,
        refreshToken: typeof nextRefresh === 'string' ? nextRefresh : refreshToken,
      }
    } catch {
      return null
    } finally {
      customerRefreshInFlight = null
    }
  })()

  return customerRefreshInFlight
}

async function refreshAdminTokens(baseURL: string): Promise<RefreshResult | null> {
  if (adminRefreshInFlight) return adminRefreshInFlight

  adminRefreshInFlight = (async () => {
    const refreshToken = getAdminRefreshToken()
    if (!refreshToken) return null

    try {
      const response = await fetch(`${baseURL}/auth/admin/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...ngrokRequestHeaders(),
        },
        body: JSON.stringify({ refreshToken }),
      })

      if (!response.ok) return null

      const body = await response.json().catch(() => ({}))
      const data = body?.data ?? body
      const accessToken = data?.token || data?.accessToken
      const nextRefresh = data?.refreshToken
      if (!accessToken || typeof accessToken !== 'string') return null

      setAdminTokens(accessToken, typeof nextRefresh === 'string' ? nextRefresh : refreshToken)
      return {
        accessToken,
        refreshToken: typeof nextRefresh === 'string' ? nextRefresh : refreshToken,
      }
    } catch {
      return null
    } finally {
      adminRefreshInFlight = null
    }
  })()

  return adminRefreshInFlight
}

function parseErrorMessage(errorData: any, statusText: string): string {
  let errorMessage = statusText
  if (errorData?.message) errorMessage = errorData.message
  if (errorData?.errors) {
    if (typeof errorData.errors === 'string') {
      errorMessage = errorData.errors
    } else if (errorData.errors.message) {
      errorMessage = errorData.errors.message
    } else if (errorData.errors.details) {
      if (errorData.errors.details.fieldErrors) {
        const fieldErrors = errorData.errors.details.fieldErrors
        if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
          const firstError = fieldErrors[0]
          if (firstError.message) {
            errorMessage = `${firstError.field}: ${firstError.message}`
          } else if (firstError.field) {
            errorMessage = `Validation error in ${firstError.field}`
          }
        }
      } else {
        errorMessage = JSON.stringify(errorData.errors.details)
      }
    }
  }
  return errorMessage
}

export class ApiClient {
  private baseURL: string

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    allowRefresh = true
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`

    const isFormData = options.body instanceof FormData

    const { headers: optHeaders, ...restOptions } = options
    const passedHeaders: Record<string, string> = (() => {
      if (!optHeaders) return {}
      if (optHeaders instanceof Headers) {
        const o: Record<string, string> = {}
        optHeaders.forEach((v, k) => {
          o[k] = v
        })
        return o
      }
      if (Array.isArray(optHeaders)) {
        return Object.fromEntries(optHeaders as [string, string][])
      }
      return { ...(optHeaders as Record<string, string>) }
    })()

    const config: RequestInit = {
      ...restOptions,
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...ngrokRequestHeaders(),
        ...passedHeaders,
      },
    }

    const isAuthEndpoint = endpoint.startsWith('/auth/')
    const isHealthEndpoint = endpoint.startsWith('/health/')
    const isPublicContactEndpoint = endpoint === '/support/contact' || endpoint.startsWith('/support/contact?')
    const isPublicNewsletterEndpoint =
      endpoint === '/marketing/newsletter/subscribe' ||
      endpoint.startsWith('/marketing/newsletter/subscribe?')

    if (!isAuthEndpoint && !isHealthEndpoint && !isPublicContactEndpoint && !isPublicNewsletterEndpoint) {
      const apiKey = apiKeyConfig.getApiKey()
      const apiSecret = apiKeyConfig.getApiSecret()

      if (!apiKey) {
        const error: ApiError = {
          message:
            'API key is required. Please configure your API key in the admin panel or set VITE_API_KEY environment variable.',
          status: 401,
        }
        throw error
      }

      const trimmedKey = apiKey.trim()
      config.headers = {
        ...config.headers,
        'X-API-Key': trimmedKey,
      }

      if (apiSecret) {
        config.headers = {
          ...config.headers,
          'X-API-Secret': apiSecret,
        }
      }
    }

    const isGuestCheckoutOtpEndpoint =
      endpoint.startsWith('/auth/customer/checkout/request-otp') ||
      endpoint.startsWith('/auth/customer/checkout/verify-otp')
    const isAdminArea = endpoint.startsWith('/admin')
    let token = isAdminArea
      ? localStorage.getItem('admin_token')
      : localStorage.getItem('auth_token')

    // Proactive customer refresh when access is near expiry
    if (
      !isAdminArea &&
      allowRefresh &&
      !shouldSkipCustomerRefresh(endpoint) &&
      token &&
      isAccessTokenExpired(token) &&
      getRefreshToken()
    ) {
      const refreshed = await refreshCustomerTokens(this.baseURL)
      if (refreshed?.accessToken) {
        token = refreshed.accessToken
      }
    }

    // Proactive admin refresh when access is near expiry
    if (
      isAdminArea &&
      allowRefresh &&
      !shouldSkipAdminRefresh(endpoint) &&
      token &&
      isAccessTokenExpired(token) &&
      getAdminRefreshToken()
    ) {
      const refreshed = await refreshAdminTokens(this.baseURL)
      if (refreshed?.accessToken) {
        token = refreshed.accessToken
      }
    }

    if (token && !isGuestCheckoutOtpEndpoint) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      }
    }

    try {
      const response = await fetch(url, config)

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        const errorMessage = parseErrorMessage(errorData, response.statusText)

        // Customer silent refresh on 401
        if (
          response.status === 401 &&
          allowRefresh &&
          !isAdminArea &&
          !shouldSkipCustomerRefresh(endpoint) &&
          getRefreshToken()
        ) {
          const refreshed = await refreshCustomerTokens(this.baseURL)
          if (refreshed?.accessToken) {
            return this.request<T>(endpoint, options, false)
          }
          clearSession()
          notifyAuthLost()
        }

        // Admin silent refresh on 401
        if (
          response.status === 401 &&
          allowRefresh &&
          isAdminArea &&
          !shouldSkipAdminRefresh(endpoint) &&
          getAdminRefreshToken()
        ) {
          const refreshed = await refreshAdminTokens(this.baseURL)
          if (refreshed?.accessToken) {
            return this.request<T>(endpoint, options, false)
          }
          clearAdminSession()
        }

        throw {
          message: errorMessage,
          status: response.status,
          errors: errorData.errors,
        } as ApiError
      }

      return await response.json()
    } catch (error) {
      if (error && typeof error === 'object' && 'status' in error) {
        throw error
      }
      console.error('API Request failed:', error)
      throw {
        message: 'Network error. Please check your connection.',
        status: 0,
      } as ApiError
    }
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' })
  }

  async post<T>(endpoint: string, data?: unknown, extraHeaders?: Record<string, string>): Promise<T> {
    const body = data instanceof FormData ? data : data ? JSON.stringify(data) : undefined

    return this.request<T>(endpoint, {
      method: 'POST',
      body,
      ...(extraHeaders ? { headers: extraHeaders } : {}),
    })
  }

  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' })
  }
}

export const apiClient = new ApiClient()

/** Exposed for tests */
export const __testables = {
  refreshCustomerTokens,
  shouldSkipCustomerRefresh,
  getAuthToken,
}
